import {makeGroupEventsOffline} from "@src/Store/eventsStore.ts";
import React, {useMemo} from "react";
import {formatMsToJpTime, strToTime} from "@src/utils/formatDate.ts";
import { Stack } from "@src/CommonComponents/Stack/Stack";
import { DataNotFound } from "@UI/illustrationsSvg/DataNotFound";
import {Body_14_Coral_Primary_Regular_14, Title_20_Coral_Strong_emphasis_20} from "@UI/typography/generated/typography";
import {Gt, LocalDTFormater} from "@src/i18n/Gt.tsx";
import {useTranslation} from "react-i18next";
import {useSearchParams} from "react-router-dom";
import {DataLoadFail} from "@UI/illustrationsSvg/DataLoadFail.tsx";
import {calculateWorkingTime} from "@src/utils/workingTimeCalculators.ts";
import {WorkDayWithEvents} from "@src/Store/employeesStore.ts";

export enum ButtonVar {
    today,
    thisWeek,
    thisMonth,
    manual
}

const presetLabels: Partial<Record<ButtonVar, string>> = {
    [ButtonVar.today]: "Today",
    [ButtonVar.thisWeek]: "This week",
    [ButtonVar.thisMonth]: "This month",
}

// Names a preset period; a custom range shows both full dates once it is applied
export const PeriodLabel = ({period}: {period: ButtonVar}) => {
    const {t} = useTranslation()
    const [params] = useSearchParams()

    if (period !== ButtonVar.manual) return <Gt>{presetLabels[period]!}</Gt>

    const from = params.get("date_range_after")
    const to = params.get("date_range_before")
    if (!from || !to) return null
    // Local midnight, so the day doesn't shift west of UTC
    return <>{t("date_range", {
        from: t(LocalDTFormater.LocalDate, {date: new Date(`${from}T00:00:00`)}),
        to: t(LocalDTFormater.LocalDate, {date: new Date(`${to}T00:00:00`)}),
    })}</>
}

export const DataNotFoundMessage = ({text}: {text?: React.ReactNode | string}) => {
    return (
        <Stack direction={"column"} alignItems={"center"}>
            <DataNotFound/>

            <Stack alignItems={"center"} justifyContent={"center"}>
                <Title_20_Coral_Strong_emphasis_20 style={{textAlign: "center", maxWidth: "270px"}}>
                    {text
                        ? text
                        : <Gt>No attendance data</Gt>
                    }
                </Title_20_Coral_Strong_emphasis_20>
            </Stack>
        </Stack>
    )
}

export const ErrorLoadingMessage = () => {
    return (
        <Stack direction="column" alignItems={"center"}>
            <DataLoadFail/>

            <Stack alignItems={"center"} justifyContent={"center"}>
                <Title_20_Coral_Strong_emphasis_20 style={{textAlign: "center", maxWidth: "270px"}}>
                    <Gt>Failed to load data</Gt>
                </Title_20_Coral_Strong_emphasis_20>
            </Stack>

        </Stack>
    )
}

export const WorkingTime = ({workdays}: {workdays: WorkDayWithEvents[]}) => {
    const workedHoursMS = useMemo(() => {
        if (!workdays || workdays?.length === 0) return 0

        let worktime = 0
        for (const workday of workdays) {
            if (workday?.total_time) {
                worktime += strToTime(workday?.total_time)
                continue
            }
            const events = makeGroupEventsOffline(workday?.events ?? [])
            worktime += calculateWorkingTime(events, 0)
        }
        return worktime
    }, [workdays])

    const workedHours = useMemo(() => {
        if (workedHoursMS === 0) return "-"
        return formatMsToJpTime(workedHoursMS)
    }, [workedHoursMS])

    return (
        <Body_14_Coral_Primary_Regular_14>
            {workedHours}
        </Body_14_Coral_Primary_Regular_14>
    )
}